import { request } from '@/api/http';

export interface MePermissions {
  canManageUsers: boolean;
  canManagePlatformUsers?: boolean;
  canManageWorkspaceMembers?: boolean;
  canViewWorkspaceUsers?: boolean;
  canApproveUsers?: boolean;
  canManageSources: boolean;
  canCreateSearch: boolean;
  canRunSearch?: boolean;
  canCreateSubscription: boolean;
  canEditSubscription?: boolean;
  canDeleteSubscription?: boolean;
  canManageRecipients?: boolean;
  canCreateRecipient?: boolean;
  canEditRecipient?: boolean;
  canDeleteRecipient?: boolean;
  canTestRecipient?: boolean;
  canManageBilling: boolean;
  canUseBillingCheckout: boolean;
  canManageConfig: boolean;
  canManageJobs: boolean;
  canViewWorkspaceJobs?: boolean;
  canManageWorkspaceJobs?: boolean;
  canViewSystemDashboard?: boolean;
  canViewRuntime: boolean;
  canViewDiagnostics?: boolean;
  canViewGlobalActivity?: boolean;
  canEditOwnProfile: boolean;
  canOpenSupportView?: boolean;
  canViewCustomerActivity?: boolean;
  canViewAdminBilling?: boolean;
  canManageAdminBilling?: boolean;
  canViewAdminRevenue?: boolean;
  canExportAdminBilling?: boolean;
  canManagePaymentProviderConfig?: boolean;
  canRunManualBillingOps?: boolean;
  canManageOrgSubscription?: boolean;
  canViewCustomerBilling?: boolean;
  canMutateCustomerBilling?: boolean;
  canManagePlatformTemplates?: boolean;
  canBrowseTemplates?: boolean;
  canInstallTemplate?: boolean;
  canManageOrgTemplates?: boolean;
  canManagePersonalTemplates?: boolean;
  canManageOrganizationTemplates?: boolean;
  canImportTemplates?: boolean;
  canExportTemplates?: boolean;
  canExportSearches?: boolean;
  canFavoriteTemplates?: boolean;
  canViewSubscriptions?: boolean;
  canManageSubscriptions?: boolean;
  canViewRecipients?: boolean;
  canViewPlatformActivity?: boolean;
  canViewWorkspaceActivity?: boolean;
  canSwitchWorkspace?: boolean;
  canBypassNotificationQuota?: boolean;
  readOnly: boolean;
}

export interface MeWorkspace {
  id: string | null;
  name: string;
  slug?: string | null;
  workspaceType?: string;
  scope: string;
  membershipRole?: string | null;
  status?: string | null;
  archivedAt?: string | null;
  deletedAt?: string | null;
}

export interface MeWorkspacesResponse {
  activeOrganizationId: string | null;
  activeWorkspaceMode: string;
  activeWorkspaceName: string;
  items: MeWorkspace[];
}

export interface MeCapabilities {
  unlimited: boolean;
  enforcePlanLimits: boolean;
  plan: string;
  maxSearches: number;
  maxSubscriptions: number;
  maxSources: number;
  maxSourcesPerSearch: number;
  scrapeIntervalMinutes: number;
  maxNotificationsPerDay: number;
  maxManualRunsPerHour: number;
  canCreateSearch: boolean;
  canRunSearch?: boolean;
  isPlatformStaff: boolean;
}

export interface MeProfile {
  id: string | null;
  role: string;
  plan?: string | null;
  status?: string;
  organizationId?: string | null;
  activeWorkspaceMode?: string;
  activeWorkspaceName?: string | null;
  workspaceCount?: number | null;
  billingBypass: boolean;
  email?: string | null;
  name?: string | null;
  membershipRole?: string | null;
  currentWorkspace?: MeWorkspace | null;
  currentWorkspacePlan?: string | null;
  permissions: MePermissions;
  termsAcceptanceRequired?: boolean;
  currentTermsVersion?: string | null;
  acceptedTermsVersion?: string | null;
}

export interface MeQuotas {
  plan: string;
  billingBypass: boolean;
  unlimited?: boolean;
  enforcePlanLimits?: boolean;
  limits: {
    maxSearches: number;
    maxSubscriptions: number;
    maxSources: number;
    maxSourcesPerSearch: number;
    scrapeIntervalMinutes: number;
    maxNotificationsPerDay: number;
    maxManualRunsPerHour: number;
  };
  usage: Record<string, number>;
  capabilities?: MeCapabilities;
}

export const meService = {
  get: () => request<MeProfile>('/me'),
  listWorkspaces: () => request<MeWorkspacesResponse>('/me/workspaces'),
  selectWorkspace: (organizationId: string | null) =>
    request<MeProfile>('/me/workspace', {
      method: 'PUT',
      body: JSON.stringify({ organizationId }),
    }),
  quotas: () => request<MeQuotas>('/me/quotas'),
  updateProfile: (body: { name: string }) =>
    request<MeProfile>('/me', { method: 'PATCH', body: JSON.stringify(body) }),
  changePassword: (body: { current_password: string; new_password: string }) =>
    request<void>('/me/password', { method: 'PATCH', body: JSON.stringify(body) }),
};
